/**
 * Kryptographische Grundbausteine – ausschließlich Web Crypto API.
 *
 * Konzept 5.1: keine Drittbibliothek für die Kernfunktionen. Die einzige
 * Ausnahme ist Argon2id (kdf.ts), weil Browser dafür nichts Eingebautes
 * mitbringen.
 *
 * Diese Datei ist sicherheitskritisch. Wer sie ändert, sollte die
 * Testvektoren in crypto.test.ts vorher und nachher laufen lassen.
 */

export const AES_ALGORITHM = 'AES-GCM';
export const AES_KEY_BITS = 256;
export const NONCE_BYTES = 12;   // von AES-GCM vorgegeben

// ---------------------------------------------------------------------------
// Kodierung
// ---------------------------------------------------------------------------

export function toBase64(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  // In Blöcken, weil String.fromCharCode(...) bei großen Arrays den Stack sprengt.
  const CHUNK = 0x8000;
  for (let offset = 0; offset < view.length; offset += CHUNK) {
    binary += String.fromCharCode(...view.subarray(offset, offset + CHUNK));
  }
  return btoa(binary);
}

export function fromBase64(value: string): Uint8Array {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

export const utf8 = {
  encode: (value: string): Uint8Array => new TextEncoder().encode(value),
  decode: (bytes: Uint8Array): string => new TextDecoder().decode(bytes),
};

export function concat(...parts: Uint8Array[]): Uint8Array {
  const total = parts.reduce((sum, part) => sum + part.length, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.length;
  }
  return result;
}

export function randomBytes(length: number): Uint8Array {
  return crypto.getRandomValues(new Uint8Array(length));
}

// ---------------------------------------------------------------------------
// Symmetrisch: AES-256-GCM
// ---------------------------------------------------------------------------

/** Erzeugt einen frischen Dokumentenschlüssel (DEK, Konzept 3.1). */
export async function generateDek(): Promise<CryptoKey> {
  return crypto.subtle.generateKey(
    { name: AES_ALGORITHM, length: AES_KEY_BITS },
    true,   // exportierbar: der DEK muss für Kunde und Buddy gewrappt werden
    ['encrypt', 'decrypt'],
  );
}

export async function importAesKey(
  raw: Uint8Array,
  usages: KeyUsage[] = ['encrypt', 'decrypt'],
): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', raw as BufferSource, AES_ALGORITHM, true, usages);
}

export async function exportAesKey(key: CryptoKey): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.exportKey('raw', key));
}

/**
 * Verschlüsselt und stellt die Nonce voran: [nonce (12) | ciphertext+tag].
 * Ein selbst gewähltes Format ist hier bewusst simpel gehalten – die Nonce
 * muss mitgeliefert werden, ist aber nicht geheim.
 */
export async function encrypt(key: CryptoKey, plaintext: Uint8Array): Promise<Uint8Array> {
  const nonce = randomBytes(NONCE_BYTES);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt(
      { name: AES_ALGORITHM, iv: nonce as BufferSource },
      key,
      plaintext as BufferSource,
    ),
  );
  return concat(nonce, ciphertext);
}

export async function decrypt(key: CryptoKey, payload: Uint8Array): Promise<Uint8Array> {
  if (payload.length <= NONCE_BYTES) {
    throw new Error('Verschlüsselter Wert ist zu kurz');
  }
  const nonce = payload.subarray(0, NONCE_BYTES);
  const ciphertext = payload.subarray(NONCE_BYTES);
  return new Uint8Array(
    await crypto.subtle.decrypt(
      { name: AES_ALGORITHM, iv: nonce as BufferSource },
      key,
      ciphertext as BufferSource,
    ),
  );
}

export async function encryptJson(key: CryptoKey, value: unknown): Promise<Uint8Array> {
  return encrypt(key, utf8.encode(JSON.stringify(value)));
}

export async function decryptJson<T>(key: CryptoKey, payload: Uint8Array): Promise<T> {
  return JSON.parse(utf8.decode(await decrypt(key, payload))) as T;
}

// ---------------------------------------------------------------------------
// Embedding-Vektoren: binär statt JSON
// ---------------------------------------------------------------------------

/**
 * Ein Embedding besteht aus mehreren hundert Fließkommazahlen. Als JSON
 * gespeichert braucht jede davon rund 20 Zeichen ("-0.034821562886238098"),
 * als 32-Bit-Zahl genau 4 Byte – gemessen ein Faktor von 5,3.
 *
 * Das ist keine Mikro-Optimierung: der Client lädt und entschlüsselt beim
 * Entsperren die Sidecars ALLER zugänglichen Dokumente. Bei 500 Dokumenten
 * sind das 7,9 MB als JSON gegenüber 1,5 MB binär – auf einer langsamen
 * Leitung der Unterschied zwischen "kurz warten" und "das Ding hängt".
 *
 * Die Genauigkeit von 32 Bit ist für Cosinus-Ähnlichkeit mehr als
 * ausreichend; die Modelle selbst rechnen intern nicht genauer.
 *
 * Byte-Reihenfolge ist explizit little-endian festgelegt, damit ein auf
 * einem Gerät geschriebener Vektor auf jedem anderen Gerät gleich gelesen
 * wird – unabhängig von dessen Architektur.
 */
export function encodeVector(values: ArrayLike<number>): Uint8Array {
  const bytes = new Uint8Array(values.length * 4);
  const view = new DataView(bytes.buffer);
  for (let i = 0; i < values.length; i += 1) {
    view.setFloat32(i * 4, values[i]!, true);
  }
  return bytes;
}

export function decodeVector(bytes: Uint8Array): Float32Array {
  if (bytes.length % 4 !== 0) {
    throw new Error('Vektor hat eine unerwartete Länge');
  }
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const values = new Float32Array(bytes.length / 4);
  for (let i = 0; i < values.length; i += 1) {
    values[i] = view.getFloat32(i * 4, true);
  }
  return values;
}

export async function encryptVector(
  key: CryptoKey,
  values: ArrayLike<number>,
): Promise<Uint8Array> {
  return encrypt(key, encodeVector(values));
}

export async function decryptVector(
  key: CryptoKey,
  payload: Uint8Array,
): Promise<Float32Array> {
  return decodeVector(await decrypt(key, payload));
}

/**
 * Mehrere Vektoren in einem Objekt – ein Dokument wird in Abschnitte zerlegt
 * und jeder Abschnitt einzeln eingebettet (siehe docs/EMBEDDINGS.md).
 *
 * Nötig, weil das Einbettungsmodell nur 512 Tokens auf einmal verarbeitet.
 * Ein zehnseitiger Vertrag passt da nicht hinein: Ohne Zerlegung bekäme er
 * gar keinen Vektor (so geschehen am 07.08.2026) oder – bei bloßem Abschneiden
 * – nur einen für die erste halbe Seite, womit alles Weitere unauffindbar wäre.
 *
 * Format: [Dimension (uint32, little-endian)] gefolgt von N × Dimension Werten.
 * Die Anzahl der Abschnitte ergibt sich aus der Länge; das Format beschreibt
 * sich damit selbst und übersteht einen Modellwechsel mit anderer Dimension.
 */
export function encodeVectors(vectors: ArrayLike<number>[]): Uint8Array {
  if (vectors.length === 0) throw new Error('Keine Vektoren übergeben');
  const dim = vectors[0]!.length;
  if (vectors.some((v) => v.length !== dim)) {
    throw new Error('Vektoren haben unterschiedliche Dimensionen');
  }

  const bytes = new Uint8Array(4 + vectors.length * dim * 4);
  const view = new DataView(bytes.buffer);
  view.setUint32(0, dim, true);
  let offset = 4;
  for (const vector of vectors) {
    for (let i = 0; i < dim; i += 1) {
      view.setFloat32(offset, vector[i]!, true);
      offset += 4;
    }
  }
  return bytes;
}

export function decodeVectors(bytes: Uint8Array): Float32Array[] {
  if (bytes.length < 4) throw new Error('Vektorblock ist zu kurz');
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const dim = view.getUint32(0, true);
  if (dim === 0 || (bytes.length - 4) % (dim * 4) !== 0) {
    throw new Error('Vektorblock hat eine unerwartete Länge');
  }

  const count = (bytes.length - 4) / (dim * 4);
  const vectors: Float32Array[] = [];
  let offset = 4;
  for (let n = 0; n < count; n += 1) {
    const vector = new Float32Array(dim);
    for (let i = 0; i < dim; i += 1) {
      vector[i] = view.getFloat32(offset, true);
      offset += 4;
    }
    vectors.push(vector);
  }
  return vectors;
}

export async function encryptVectors(
  key: CryptoKey,
  vectors: ArrayLike<number>[],
): Promise<Uint8Array> {
  return encrypt(key, encodeVectors(vectors));
}

export async function decryptVectors(
  key: CryptoKey,
  payload: Uint8Array,
): Promise<Float32Array[]> {
  return decodeVectors(await decrypt(key, payload));
}
